import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useApi } from '../api/useApi';
import { useSession } from '../auth/SessionProvider';
import type { ChangelogEntry, ChangelogResponse } from '../api/types';

// Kategorie → Badge-Farbe (Fluent-Badge-Paletten).
const CAT_COLOR: Record<string, 'brand' | 'success' | 'danger' | 'warning' | 'informative' | 'subtle'> = {
  'Neue Funktionen': 'success',
  Fehlerbehebungen: 'danger',
  Performance: 'brand',
  Refactoring: 'informative',
  'Infrastruktur & Betrieb': 'informative',
  Wartung: 'subtle',
  Dokumentation: 'subtle',
  Tests: 'subtle',
  Styling: 'subtle',
  Sonstiges: 'subtle',
};

export function ChangelogPage() {
  const api = useApi();
  const { me } = useSession();
  const [data, setData] = useState<ChangelogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    api
      .getChangelog()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [api]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    data?.entries.forEach((e) => set.add(e.category));
    return [...set];
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.entries ?? []).filter((e) => {
      if (category && e.category !== category) return false;
      if (!q) return true;
      return (
        e.subject.toLowerCase().includes(q) ||
        e.version.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        (e.scope ?? '').toLowerCase().includes(q)
      );
    });
  }, [data, search, category]);

  if (me && !me.isAdmin) {
    return (
      <div>
        <PageHeader title="Update-Verlauf" />
        <MessageBar intent="warning">
          <MessageBarBody>Der Update-Verlauf ist M365 Adminbox-Admins vorbehalten.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
      <PageHeader title="Update-Verlauf" description="Technischer Änderungsverlauf — automatisch aus den Builds erzeugt." />

      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' }}>
        {data?.current ? <Badge appearance="filled" color="brand" size="large">v{data.current}</Badge> : null}
        {data ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{data.count} Einträge</Caption1> : null}
      </div>

      {error ? (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      ) : null}

      {loading ? (
        <Spinner label="Lade Verlauf…" />
      ) : (
        <>
          <Input
            placeholder="Suchen (Text, Version, Kategorie, Bereich)…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<SearchRegular />}
            contentAfter={
              search ? <DismissRegular style={{ cursor: 'pointer' }} onClick={() => setSearch('')} aria-label="Suche leeren" /> : undefined
            }
          />

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <FilterChip label="Alle" active={category === null} onClick={() => setCategory(null)} />
            {categories.map((c) => (
              <FilterChip key={c} label={c} active={category === c} onClick={() => setCategory(c)} />
            ))}
          </div>

          {filtered.length === 0 ? (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>Keine Einträge gefunden.</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((e) => (
                <Entry key={e.sha + e.version} e={e} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${active ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
        background: active ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        borderRadius: 999,
        padding: '3px 12px',
        fontSize: 12.5,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function Entry({ e }: { e: ChangelogEntry }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'baseline',
        padding: '10px 12px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
        background: tokens.colorNeutralBackground1,
      }}
    >
      <Badge appearance="tint" color="brand" style={{ fontFamily: "'Share Tech Mono', monospace", flexShrink: 0 }}>
        v{e.version}
      </Badge>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge appearance="outline" color={CAT_COLOR[e.category] ?? 'subtle'} size="small">{e.category}</Badge>
          {e.scope ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{e.scope}</Caption1> : null}
          {e.breaking ? <Badge appearance="filled" color="danger" size="small">Breaking</Badge> : null}
        </div>
        <Text size={300} style={{ display: 'block', marginTop: 2 }}>{e.subject}</Text>
      </div>
      <Caption1 style={{ color: tokens.colorNeutralForeground4, flexShrink: 0 }}>{e.date}</Caption1>
    </div>
  );
}
